import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, User, Calendar, MapPin } from "lucide-react";
import "./ArticleDetails.css";
import articlesData from "../../data/articles.json";
import SEO from "../../components/SEO";
import { authorPersonSchema, getAuthor } from "../../data/authors";
import Breadcrumb from "../../components/Breadcrumb";
import { toArabicSlug } from "../../data/areaSlugMapping";

function ArticleDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Find the current article
    const currentArticleIndex = articlesData.articles.findIndex(a => a.slug === slug);

    if (currentArticleIndex === -1) {
      // Article not found
      navigate('/articles');
      return;
    }

    const currentArticle = articlesData.articles[currentArticleIndex];
    setArticle(currentArticle);

    // Find previous and next articles
    if (currentArticleIndex > 0) {
      setPrevArticle(articlesData.articles[currentArticleIndex - 1]);
    } else {
      setPrevArticle(null);
    }

    if (currentArticleIndex < articlesData.articles.length - 1) {
      setNextArticle(articlesData.articles[currentArticleIndex + 1]);
    } else {
      setNextArticle(null);
    }

    // Find related articles (excluding current article)
    if (currentArticle.tags && currentArticle.tags.length > 0) {
      const related = articlesData.articles
        .filter(a =>
          a.id !== currentArticle.id &&
          a.tags.some(tag => currentArticle.tags.includes(tag))
        )
        .slice(0, 3);

      setRelatedArticles(related);
    }

    setLoading(false);
  }, [slug, navigate]);

  // Determine covered areas for contextual bidirectional linking
  const coveredAreas = useMemo(() => {
    if (!article) return [];
    const fullText = `${article.title} ${article.summary} ${(article.tags || []).join(' ')} ${article.content || ''}`
      .replace(/[أإآ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي');

    const primaryAreas = [
      "التجمع الخامس", "شارع التسعين", "التجمع الأول", "التجمع الثالث",
      "مدينة نصر", "عباس العقاد", "مكرم عبيد", "مصطفى النحاس",
      "مصر الجديدة", "روكسي", "شيراتون", "النزهة",
      "المعادي", "زهراء المعادي", "صقر قريش",
      "6 أكتوبر", "الشيخ زايد", "حدائق الأهرام",
      "الدقي", "المهندسين", "العجوزة", "الجيزة", "الهرم", "فيصل",
      "المقطم", "الهضبة الوسطى", "مدينتي", "الرحاب", "الشروق", "العبور", "بدر",
      "طريق السويس", "طريق العين السخنة", "الطريق الدائري", "محور 26 يوليو",
      "الإسكندرية", "سموحة", "الساحل الشمالي", "العلمين الجديدة", "الزقازيق"
    ];

    const matched = [];
    if (article.targetArea && !matched.includes(article.targetArea)) {
      matched.push(article.targetArea);
    }

    for (const a of primaryAreas) {
      const normA = a.replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي');
      if (fullText.includes(normA) && !matched.includes(a)) {
        matched.push(a);
      }
    }

    if (matched.length === 0) {
      return ["التجمع الخامس", "مدينة نصر", "مصر الجديدة", "المعادي", "6 أكتوبر", "الشيخ زايد", "الطريق الدائري", "طريق السويس"];
    }

    return matched.slice(0, 8);
  }, [article]);

  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('ar-EG', options);
  };

  // Helper to format inline markdown bold and links
  const formatInlineText = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="article-inner-link" style="color: #0077ff; font-weight: 700; text-decoration: underline;">$1</a>');
  };

  // Function to render article content with proper formatting
  const renderContent = (content) => {
    if (!content) return null;

    return content.split('\n\n').map((paragraph, idx) => {
      const trimmed = paragraph.trim();

      if (trimmed === '---') {
        return <hr key={idx} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '2rem 0' }} />;
      } else if (trimmed.startsWith('## ')) {
        const lines = trimmed.split('\n');
        const heading = lines[0].replace('## ', '');
        const rest = lines.slice(1).join('\n').trim();
        return (
          <React.Fragment key={idx}>
            <h2>{heading}</h2>
            {rest && <p dangerouslySetInnerHTML={{ __html: formatInlineText(rest) }} />}
          </React.Fragment>
        );
      } else if (trimmed.startsWith('### ')) {
        const lines = trimmed.split('\n');
        const heading = lines[0].replace('### ', '');
        const rest = lines.slice(1).join('\n').trim();
        return (
          <React.Fragment key={idx}>
            <h3>{heading}</h3>
            {rest && <p dangerouslySetInnerHTML={{ __html: formatInlineText(rest) }} />}
          </React.Fragment>
        );
      } else if (trimmed.startsWith('|') && trimmed.includes('\n|')) {
        // Parse markdown table
        const lines = trimmed.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|'));
        if (lines.length >= 2) {
          const headerCols = lines[0].split('|').map((c) => c.trim()).filter(Boolean);
          const bodyLines = lines.slice(1).filter((l) => !l.includes('---'));
          return (
            <div key={idx} style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', border: '1px solid #cbd5e1' }}>
                <thead>
                  <tr style={{ background: '#0a2c61', color: '#ffffff' }}>
                    {headerCols.map((th, hIdx) => (
                      <th
                        key={hIdx}
                        style={{ padding: '0.8rem 1rem', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                        dangerouslySetInnerHTML={{ __html: formatInlineText(th) }}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyLines.map((row, rIdx) => {
                    const cells = row.split('|').map((c) => c.trim()).filter(Boolean);
                    return (
                      <tr key={rIdx} style={{ background: rIdx % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                        {cells.map((td, cIdx) => (
                          <td
                            key={cIdx}
                            style={{ padding: '0.75rem 1rem', border: '1px solid #cbd5e1', fontSize: '0.92rem', color: '#1e293b' }}
                            dangerouslySetInnerHTML={{ __html: formatInlineText(td) }}
                          />
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={idx} dangerouslySetInnerHTML={{ __html: formatInlineText(paragraph) }} />;
      } else if (paragraph.includes('\n- ')) {
        const listItems = paragraph.split('\n- ');
        const listTitle = listItems.shift();

        return (
          <React.Fragment key={idx}>
            {listTitle && <p dangerouslySetInnerHTML={{ __html: formatInlineText(listTitle) }} />}
            <ul>
              {listItems.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: formatInlineText(item) }} />
              ))}
            </ul>
          </React.Fragment>
        );
      } else if (paragraph.includes('\n1. ')) {
        const listItems = paragraph.split(/\n\d+\. /);
        const listTitle = listItems.shift();

        return (
          <React.Fragment key={idx}>
            {listTitle && <p dangerouslySetInnerHTML={{ __html: formatInlineText(listTitle) }} />}
            <ol>
              {listItems.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: formatInlineText(item) }} />
              ))}
            </ol>
          </React.Fragment>
        );
      } else {
        return (
          <p
            key={idx}
            dangerouslySetInnerHTML={{ __html: formatInlineText(paragraph) }}
          />
        );
      }
    });
  };

  if (loading) {
    return (
      <div className="article-details">
        <p>جاري التحميل...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="article-details">
        <p>المقال غير موجود</p>
        <Link to="/articles" className="back-to-articles">
          <ChevronRight size={18} /> العودة إلى المقالات
        </Link>
      </div>
    );
  }

  return (
    <div className="article-details">
      <SEO
        title={article.title}
        description={article.summary}
        path={`/articles/${article.slug}`}
        image={article.image || "/logo.png"}
        type="article"
        appendBrand={article.title.length < 45}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: article.summary,
          image: `https://winchmohamedbahr.com${article.image || "/logo.png"}`,
          datePublished: article.date,
          dateModified: article.lastUpdated || article.date,
          author: authorPersonSchema(article.author),
          publisher: {
            "@type": "Organization",
            name: "ونش انقاذ سيارات محمد بحر",
            logo: { "@type": "ImageObject", url: "https://winchmohamedbahr.com/logo.png" },
          },
          mainEntityOfPage: `https://winchmohamedbahr.com/articles/${article.slug}`,
        }}
      />
      <Link to="/articles" className="back-to-articles">
        <ChevronRight size={18} /> العودة إلى المقالات
      </Link>

      <Breadcrumb
        items={[{ label: "المقالات", path: "/articles" }, { label: article.title, path: `/articles/${article.slug}` }]}
        path={`/articles/${article.slug}`}
      />

      <div className="article-details-header">
        <h1 className="article-details-title">{article.title}</h1>

        <div className="article-details-meta">
          <div className="article-details-date">
            <Calendar size={16} /> نُشر في {formatDate(article.date)}
          </div>
          {article.lastUpdated && article.lastUpdated !== article.date && (
            <div className="article-details-updated" style={{ marginRight: "1rem", color: "#1976d2", fontWeight: 600 }}>
              آخر تحديث: {formatDate(article.lastUpdated)}
            </div>
          )}
          <div className="article-details-author">
            <User size={16} />{" "}
            <Link to={`/about#team-${getAuthor(article.author).slug}`} style={{ color: "#0a2c61", textDecoration: "none" }}>
              {article.author}
            </Link>
          </div>
        </div>

        <div className="article-details-tags">
          {article.tags.map((tag, idx) => (
            <span key={idx} className="article-details-tag">{tag}</span>
          ))}
        </div>
      </div>

      <div className="article-details-image">
        <img
          src={article.image || "/images/articles/default.jpg"}
          alt={article.title}
          width="800"
          height="450"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/images/articles/default.jpg";
          }}
        />
      </div>

      {/* Dedicated Target Area Emergency Callout Banner */}
      {article.targetArea && (
        <div className="article-dedicated-target-banner" style={{
          background: 'linear-gradient(135deg, #0a2c61 0%, #0077ff 100%)',
          color: '#ffffff',
          borderRadius: '1.2rem',
          padding: '1.5rem 2rem',
          margin: '2rem 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1.2rem',
          direction: 'rtl',
          boxShadow: '0 8px 25px rgba(0, 119, 255, 0.2)'
        }}>
          <div>
            <span style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '0.3rem 0.8rem',
              borderRadius: '1rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              marginBottom: '0.5rem'
            }}>
              📍 دليل ميداني مخصص لـ {article.targetArea}
            </span>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, margin: '0 0 0.3rem 0', color: '#ffffff' }}>
              هل تحتاج ونش إنقاذ الآن في {article.targetArea}؟
            </h3>
            <p style={{ margin: 0, fontSize: '0.95rem', opacity: 0.9 }}>
              أوناشنا متمركزة حالياً في {article.targetArea} وتصلك خلال دقائق معدودة بخصم 50%
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Link
              to={`/areas/${toArabicSlug(article.targetArea)}`}
              style={{
                background: '#ffffff',
                color: '#0a2c61',
                padding: '0.65rem 1.4rem',
                borderRadius: '2rem',
                fontWeight: 800,
                textDecoration: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'transform 0.2s'
              }}
            >
              صفحة ونش إنقاذ {article.targetArea} ⬅
            </Link>
            <a
              href="tel:01044844492"
              style={{
                background: '#ef4444',
                color: '#ffffff',
                padding: '0.65rem 1.4rem',
                borderRadius: '2rem',
                fontWeight: 800,
                textDecoration: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(239,68,68,0.3)'
              }}
            >
              اتصل الآن 01044844492
            </a>
          </div>
        </div>
      )}

      <div className="article-content-wrapper">
        <div className="article-details-content">
          {renderContent(article.content)}
        </div>
      </div>

      {/* Covered Service Areas for Deep Topical Interlinking */}
      {coveredAreas.length > 0 && (
        <div className="article-covered-areas" style={{
          background: '#f8fafc',
          borderRadius: '1.2rem',
          padding: '1.8rem',
          margin: '2rem 0',
          border: '2px solid #e2e8f0',
          direction: 'rtl'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
            <MapPin size={22} color="#0077ff" />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0a2c61', margin: 0 }}>
              مناطق الخدمة والتغطية المشمولة بهذا الدليل
            </h3>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.98rem', margin: '0 0 1.2rem 0' }}>
            نوفر أوناش إنقاذ متمركزة وفورية تخدم المناطق المذكورة في هذا المقال على مدار 24 ساعة بخصم 50%:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem' }}>
            {coveredAreas.map((areaName, idx) => (
              <Link
                key={idx}
                to={`/areas/${toArabicSlug(areaName)}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#0a2c61',
                  padding: '0.5rem 1.1rem',
                  borderRadius: '2rem',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                }}
              >
                <span>📍 ونش إنقاذ {areaName}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="article-navigation">
        {prevArticle ? (
          <Link to={`/articles/${prevArticle.slug}`} className="article-nav-button">
            <ChevronRight size={18} /> المقال السابق
          </Link>
        ) : (
          <span className="article-nav-button disabled">
            <ChevronRight size={18} /> المقال السابق
          </span>
        )}

        {nextArticle ? (
          <Link to={`/articles/${nextArticle.slug}`} className="article-nav-button">
            المقال التالي <ChevronLeft size={18} />
          </Link>
        ) : (
          <span className="article-nav-button disabled">
            المقال التالي <ChevronLeft size={18} />
          </span>
        )}
      </div>

      {relatedArticles.length > 0 && (
        <div className="related-articles">
          <h3 className="related-articles-title">مقالات ذات صلة</h3>
          <div className="related-articles-grid">
            {relatedArticles.map((relatedArticle) => (
              <div key={relatedArticle.id} className="article-card">
                <div className="article-card-image">
                  <img
                    src={relatedArticle.image || "/images/articles/default.jpg"}
                    alt={relatedArticle.title}
                    loading="lazy"
                    width="400"
                    height="225"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/articles/default.jpg";
                    }}
                  />
                </div>
                <div className="article-card-content">
                  <h3 className="article-card-title">{relatedArticle.title}</h3>
                  <Link to={`/articles/${relatedArticle.slug}`} className="article-read-more">
                    اقرأ المزيد <ChevronLeft size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Internal-link footer: related services + areas */}
      <div className="article-internal-links" style={{
        marginTop: "2.5rem", padding: "1.5rem", background: "#f5f8ff",
        borderRadius: "1rem", border: "1px solid #d6e4f5"
      }}>
        <h3 style={{ margin: "0 0 1rem", color: "#0a2c61", fontSize: "1.1rem" }}>
          اطلب الخدمة الآن أو تصفّح خدماتنا
        </h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 1rem", direction: "rtl" }}>
          <Link to="/service/rescue" style={ilStyle}>ونش إنقاذ السيارات</Link>
          <Link to="/service/equipment" style={ilStyle}>نقل المعدات</Link>
          <Link to="/service/fuel" style={ilStyle}>التزود بالوقود</Link>
          <Link to="/service/battery" style={ilStyle}>وصلة بطارية</Link>
          <Link to="/service/tires" style={ilStyle}>تغيير الإطارات</Link>
          <Link to="/areas" style={ilStyle}>جميع مناطق الخدمة</Link>
          <Link to="/contact" style={ilStyle}>اتصل بنا</Link>
        </div>
      </div>
    </div>
  );
}

const ilStyle = {
  background: "#0a2c61",
  color: "#fff",
  padding: "0.4rem 0.9rem",
  borderRadius: "0.5rem",
  textDecoration: "none",
  fontSize: "0.9rem",
  fontWeight: 600,
};

export default ArticleDetails;
